import React, { useEffect, useState } from "react";
import { GitCommit, GitPullRequest } from "lucide-react";

import { apiGet } from "../Api/Client.js";
import "../Styles/TaskGitLinks.css";

// Four, not two: any commit message that merely mentions a task code is linked
// to it (that's the convention, and it can't tell "worked on" from "talked
// about"), so a couple of incidental mentions must not push the real work
// into "+N more".
const SHOWN_COMMITS = 4;

// The commits and pull requests behind tasks, any branch -- fetched as its own
// request so a slow GitHub round trip never holds up the task list itself.
// Pass a project id on the Projects board to get the whole project's tasks;
// leave it off for "my tasks" (home page, project cards).
export function useTaskGitLinks(projectId) {
  const [links, setLinks] = useState({});
  useEffect(() => {
    let cancelled = false;
    // Another project: drop the last project's links at once rather than showing them until the answer lands.
    setLinks({});
    apiGet(`/Project/task-commits/${projectId ? `?project=${projectId}` : ""}`)
      .then((res) => { if (!cancelled) setLinks(res?.links || {}); })
      .catch(() => { if (!cancelled) setLinks({}); });
    return () => { cancelled = true; };
  }, [projectId]);
  return links;
}

export function hasTaskGitLinks(links) {
  return Boolean(links && ((links.commits || []).length || (links.pull_requests || []).length));
}

// A line of small chips: PRs first (the larger unit of work), then the most
// recent commits. Each opens GitHub in a new tab; the tooltip carries what a
// chip is too small to show (message, branch, who, when).
export function TaskGitLinks({ links }) {
  if (!hasTaskGitLinks(links)) return null;
  const commits = links.commits || [];
  const pulls = links.pull_requests || [];
  const extra = commits.length - SHOWN_COMMITS;
  const stop = (e) => e.stopPropagation();

  return (
    <div className="TaskGit" onClick={stop}>
      {pulls.map((pr) => (
        <a key={`pr-${pr.repo}-${pr.number}`} className={`TaskGit-Chip pr ${pr.state}`} href={pr.url} target="_blank" rel="noopener noreferrer"
           title={`${pr.title}\n${pr.repo} · ${pr.branch}`}>
          <GitPullRequest size={11} /> PR #{pr.number} · {pr.state}
        </a>
      ))}
      {commits.slice(0, SHOWN_COMMITS).map((c) => (
        <a key={`${c.repo}-${c.sha}`} className="TaskGit-Chip" href={c.url} target="_blank" rel="noopener noreferrer"
           title={`${c.title}\n${c.repo} · ${c.branches.join(", ")}${c.author ? ` · ${c.author}` : ""}\n${c.when ? new Date(c.when).toLocaleString() : ""}`}>
          <GitCommit size={11} /> <span className="TaskGit-Sha">{c.sha}</span>
          <span className="TaskGit-Branch">{c.branches[0]}</span>
        </a>
      ))}
      {extra > 0 && <span className="TaskGit-More" title={commits.slice(SHOWN_COMMITS).map((c) => `${c.sha} ${c.title}`).join("\n")}>+{extra} more</span>}
    </div>
  );
}
